const CONSENT_STORAGE_KEY = "lxl_analytics_consent"
const SCRIPT_ID = "lxl-google-analytics"
const CONVERSION_TRACKER_KEY = "__lxlConversionTracking"
const DEFAULT_MEASUREMENT_ID = "G-YQH492Q1ML"
const ATTRIBUTION_PARAMETERS = new Set([
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "gclid",
  "gbraid",
  "wbraid",
  "msclkid",
])
let analyticsConfigured = false
let lastTrackedPage = null
let inMemoryConsent = null

const getMeasurementId = () =>
  process.env.GATSBY_GOOGLE_ANALYTICS_ID || DEFAULT_MEASUREMENT_ID

const isBrowser = () => typeof window !== "undefined"

const stripUrlParameters = value => {
  try {
    const url = new URL(value, window.location.origin)
    return `${url.origin}${url.pathname}`
  } catch {
    return ""
  }
}

const getAttributionSafeUrl = value => {
  try {
    const url = new URL(value, window.location.origin)
    const safeUrl = new URL(`${url.origin}${url.pathname}`)

    url.searchParams.forEach((parameterValue, parameterName) => {
      if (ATTRIBUTION_PARAMETERS.has(parameterName)) {
        safeUrl.searchParams.append(parameterName, parameterValue)
      }
    })

    return safeUrl.toString()
  } catch {
    return ""
  }
}

const getSafePageContext = () => ({
  page_location: getAttributionSafeUrl(window.location.href),
  page_path: window.location.pathname,
  ...(document.referrer
    ? { page_referrer: stripUrlParameters(document.referrer) }
    : {}),
})

const initializeDataLayer = () => {
  if (!isBrowser()) return

  window.dataLayer = window.dataLayer || []
  window.gtag =
    window.gtag ||
    function gtag() {
      window.dataLayer.push(arguments)
    }
}

const configureAnalytics = () => {
  const measurementId = getMeasurementId()
  if (!measurementId || !isBrowser()) return

  initializeDataLayer()
  window.gtag("consent", analyticsConfigured ? "update" : "default", {
    analytics_storage: "granted",
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
  })

  if (!analyticsConfigured) {
    window.gtag("js", new Date())
    window.gtag("config", measurementId, {
      anonymize_ip: true,
      ...getSafePageContext(),
      send_page_view: false,
    })
    analyticsConfigured = true
  }

  trackPageView(window.location)
}

export const getAnalyticsConsent = () => {
  if (!isBrowser()) return "granted"
  if (inMemoryConsent === "granted" || inMemoryConsent === "denied") {
    return inMemoryConsent
  }

  try {
    const savedConsent = window.localStorage.getItem(CONSENT_STORAGE_KEY)
    return savedConsent === "granted" || savedConsent === "denied"
      ? savedConsent
      : "granted"
  } catch {
    return "granted"
  }
}

export const loadAnalytics = () => {
  const measurementId = getMeasurementId()

  if (!measurementId || !isBrowser() || getAnalyticsConsent() !== "granted") {
    return
  }

  configureAnalytics()

  if (document.getElementById(SCRIPT_ID)) {
    return
  }

  const script = document.createElement("script")
  script.id = SCRIPT_ID
  script.async = true
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(
    measurementId
  )}`
  document.head.appendChild(script)
}

export const setAnalyticsConsent = consent => {
  if (!isBrowser()) return

  inMemoryConsent = consent
  try {
    window.localStorage.setItem(CONSENT_STORAGE_KEY, consent)
  } catch {
    // The in-memory preference still applies for the current document.
  }

  if (consent === "granted") {
    loadAnalytics()
    return
  }

  if (window.gtag) {
    window.gtag("consent", "update", {
      analytics_storage: "denied",
      ad_storage: "denied",
      ad_user_data: "denied",
      ad_personalization: "denied",
    })
  }

  const cookieDomains = ["", window.location.hostname, "libraxlibramx.com"]

  document.cookie.split(";").forEach(cookie => {
    const cookieName = cookie.split("=")[0].trim()
    if (cookieName === "_ga" || cookieName.startsWith("_ga_")) {
      cookieDomains.forEach(domain => {
        const domainAttribute = domain ? `; domain=.${domain}` : ""
        document.cookie = `${cookieName}=; Max-Age=0; path=/${domainAttribute}; SameSite=Lax`
      })
    }
  })
}

export const trackEvent = (eventName, parameters = {}) => {
  if (
    !isBrowser() ||
    getAnalyticsConsent() !== "granted" ||
    typeof window.gtag !== "function"
  ) {
    return
  }

  window.gtag("event", eventName, {
    ...parameters,
    ...getSafePageContext(),
  })
}

export const trackPageView = location => {
  if (
    !location ||
    !isBrowser() ||
    !analyticsConfigured ||
    getAnalyticsConsent() !== "granted" ||
    typeof window.gtag !== "function"
  ) {
    return
  }

  const pagePath = location.pathname
  if (lastTrackedPage === pagePath) return
  lastTrackedPage = pagePath

  trackEvent("page_view", {
    page_title: document.title,
  })
}

const classifyTrackedLink = anchor => {
  const rawHref = anchor.getAttribute("href") || ""

  if (/^(https?:\/\/)?(api\.whatsapp\.com|wa\.me)\//i.test(rawHref)) {
    return { eventName: "generate_lead", method: "whatsapp" }
  }

  if (rawHref.startsWith("tel:")) {
    return { eventName: "generate_lead", method: "phone" }
  }

  if (rawHref.startsWith("mailto:")) {
    return { eventName: "generate_lead", method: "email" }
  }

  if (
    /maps\.app\.goo\.gl|google\.[^/]+\/maps|maps\.apple\.com/i.test(rawHref)
  ) {
    return { eventName: "directions_click", method: "maps" }
  }

  try {
    const url = new URL(anchor.href, window.location.origin)
    if (
      url.origin === window.location.origin &&
      /^\/cita\/?$/.test(url.pathname)
    ) {
      return { eventName: "booking_cta_click", method: "website" }
    }
  } catch {
    return null
  }

  return null
}

const getSafeLinkDestination = anchor => {
  const href = anchor.getAttribute("href") || ""

  if (href.startsWith("tel:")) return "tel:"
  if (href.startsWith("mailto:")) return "mailto:"

  try {
    return stripUrlParameters(anchor.href)
  } catch {
    return "other"
  }
}

export const installConversionTracking = () => {
  if (!isBrowser()) return () => {}

  if (!document[CONVERSION_TRACKER_KEY]) {
    const handleClick = event => {
      if (!(event.target instanceof Element)) return

      const anchor = event.target.closest("a[href]")
      if (!anchor || anchor.hasAttribute("data-analytics-ignore")) return

      const trackedLink = classifyTrackedLink(anchor)
      if (!trackedLink) return
      if (
        /^\/privacidad\/?$/.test(window.location.pathname) &&
        ["email", "phone"].includes(trackedLink.method)
      ) {
        return
      }

      trackEvent(trackedLink.eventName, {
        contact_method: trackedLink.method,
        link_destination: getSafeLinkDestination(anchor),
      })
    }

    document.addEventListener("click", handleClick)
    document[CONVERSION_TRACKER_KEY] = {
      handleClick,
      subscribers: 0,
    }
  }

  const trackingState = document[CONVERSION_TRACKER_KEY]
  trackingState.subscribers += 1
  let isSubscribed = true

  return () => {
    if (!isSubscribed) return
    isSubscribed = false

    const currentState = document[CONVERSION_TRACKER_KEY]
    if (!currentState) return

    currentState.subscribers -= 1
    if (currentState.subscribers <= 0) {
      document.removeEventListener("click", currentState.handleClick)
      delete document[CONVERSION_TRACKER_KEY]
    }
  }
}

export const hasAnalyticsConfiguration = () => Boolean(getMeasurementId())
